import { every, get, has, isEmpty, isNull, some } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

import { IDropDownOption } from 'shared/interface';

import {
	IAllMaterialType,
	IAllNewsType,
	IForecastGroupData,
	ILoginsInfo,
	IMaterDataDownload,
	IMaterialGroupData,
	IMaterialObj,
	IMaterialObjChildren,
	INewsGroupData,
	IPdfModuleGroupData,
	IUnitInterface
} from 'features/admin/interface/admin';
import {
	API_CONFIG,
	downloadAdminDataMaterial,
	downloadAdminMasterDataHeaders,
	downloadAuthorizationBackpack,
	downloadAuthorizationUser,
	downloadCountriesAndRegions,
	downloadCurrenciesHeader,
	downloadHeader,
	downloadMaterialGroupHeaders,
	downloadNotations,
	downloadTenadersCountriesHeaders,
	downloadUnitsHeaders,
	downloadUserLogins
} from 'shared/constants/constants';
import httpService from 'shared/services/http.service';
import { formatDate } from 'shared/util/utility';
import moment from 'moment';
import { ICostModelGroup, ICostModelList, ICostModelListEntry, ICostModelObj } from '../interface/costModel';

export const handleMaterialGroup = (
	item: IMaterialGroupData[],
	type: string,
	isUser?: boolean,
	startDate?: Date | null,
	endDate?: Date | null,
	listDate?: string | null
) => {
	const tempArray: any = [];
	const tempHandleMaterialGroup = (item: IMaterialGroupData[], type: string) => {
		item.forEach((item: IMaterialGroupData) => {
			if (item.type === type) {
				const tempItem =
					type === 'list'
						? item
						: isUser
						? {
								symbol: item.symbol,
								boerse: item.boerse,
								startDate: startDate ? formatDate(startDate, 'YYYY-MM-DD') : listDate,
								endDate: endDate ? formatDate(endDate, 'YYYY-MM-DD') : listDate
						  }
						: {
								symbol: item.symbol,
								boerse: item.boerse
						  };
				tempArray.push(tempItem);
			} else {
				tempHandleMaterialGroup(item.children, type);
			}
		});
	};
	tempHandleMaterialGroup(item, type);
	return tempArray;
};

export const gatherIdsInArray = (
	obj: any,
	arrayOfIds: { [key: string]: string | Date | null }[],
	arrayOfListIds: string[],
	isPushIntoArray: boolean,
	isSearchEnabled: boolean,
	key: string,
	date: Date | null,
	isUser: boolean
) => {
	if (obj.isChecked && !has(obj, 'children') && (obj as any).type === 'list_entries' && isPushIntoArray) {
		const dateObj: { [key: string]: string | Date | null } = {
			symbol: ((obj as any).symbol && (obj as any).symbol) || '',
			boerse: ((obj as any).boerse && (obj as any).boerse) || ''
		};

		if (isUser) {
			dateObj.startDate =
				key === 'startDate' ? formatDate(date, 'YYYY-MM-DD') || null : formatDate((obj as any).startDate, 'YYYY-MM-DD') || null;
			dateObj.endDate = key === 'endDate' ? formatDate(date, 'YYYY-MM-DD') || null : formatDate((obj as any).endDate, 'YYYY-MM-DD') || null;
		}

		arrayOfIds.push(dateObj);
	}
	if (obj.isChecked && obj.type === 'list' && !isSearchEnabled) {
		arrayOfListIds.push(`${obj.id}`);
	}
	if (has(obj, 'children')) {
		if (obj.isChecked) {
			isPushIntoArray = isSearchEnabled;
		}
		for (const subChildKey in obj.children) {
			gatherIdsInArray(obj.children[subChildKey], arrayOfIds, arrayOfListIds, isPushIntoArray, isSearchEnabled, key, date, isUser);
		}
	}
};

export const gatherIdsInArrayForCostModel = (
	obj: any,
	arrayOfIds: Array<{
		symbol: string;
		boerse: string;
		startDate?: string | null;
		endDate?: string | null;
	}>,
	arrayOfListIds: string[],
	isPushIntoArray: boolean,
	isSearchEnabled: boolean,
	groupIds: number[],
	isUser: boolean,
	key: string,
	date: Date | null
) => {
	if (obj.type === 'group' && obj.isChecked) {
		// If object is a group and is checked, add to groupIds
		groupIds.push(obj.id);
	}
	// If object is 'list'
	if (obj.type === 'list') {
		let hasCheckedChildren = false;

		// Check if any children are list_entries and checked
		if (has(obj, 'children')) {
			for (const childKey in obj.children) {
				const child = obj.children[childKey];
				if (child.isChecked && child.type === 'list_entries') {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					hasCheckedChildren = true;
					// Push checked child to arrayOfIds
					if (!isUser) {
						arrayOfIds.push({
							symbol: child.symbol || '',
							boerse: child.boerse || ''
						});
					} else {
						arrayOfIds.push({
							symbol: child?.symbol || '',
							boerse: child?.boerse || '',
							startDate:
								key === 'startDate' ? formatDate(date, 'YYYY-MM-DD') || null : formatDate(child.startDate, 'YYYY-MM-DD') || null,
							endDate: key === 'endDate' ? formatDate(date, 'YYYY-MM-DD') || null : formatDate(child.endDate, 'YYYY-MM-DD') || null
						});
					}
				}
			}
		}

		// Push parent ID to arrayOfListIds only if parent is checked
		if (obj.isChecked) {
			arrayOfListIds.push(`${obj.id}`);
		}
	}

	// Handle child recursion
	if (has(obj, 'children')) {
		for (const subChildKey in obj.children) {
			gatherIdsInArrayForCostModel(
				obj.children[subChildKey],
				arrayOfIds,
				arrayOfListIds,
				isPushIntoArray,
				isSearchEnabled,
				groupIds,
				isUser,
				key,
				date
			);
		}
	}
};

export const convertArrayToKeyValue = (arr: IMaterialGroupData[], isBackpack: boolean) => {
	return arr.reduce((result: any, item: IMaterialGroupData) => {
		const {
			id,
			children,
			type,
			is_backpack,
			boerse,
			symbol,
			gueltig_bis,
			gueltig_ab,
			isForecastAllowed,
			status,
			hasForecast,
			isForecastAllowedByBackpack
		} = item;
		const childrenObject = isEmpty(get(item, 'children', [])) ? false : convertArrayToKeyValue(children, isBackpack);
		if (type !== 'list_entries') {
			result[`${id}${type}`] = {
				isChecked: false,
				isLocked: !isEmpty(get(item, 'children', [])) ? false : true,
				isBackpackAuthorized: !isEmpty(get(item, 'children', [])) || isBackpack ? false : true,
				isAllselectedId: false,
				children: childrenObject,
				type: type,
				id: id,
				isForecastLocked: false,
				isForecastBackpackAuthorized: false,
				hasForecast: false
			};
		} else {
			result[`${id}${type}`] = {
				date: gueltig_bis ? new Date(gueltig_bis) : null,
				startDate: gueltig_ab ? new Date(gueltig_ab) : null,
				endDate: gueltig_bis ? new Date(gueltig_bis) : null,
				isLocked: status === 'A',
				isInque: status === 'Q',
				isBackpackAuthorized: is_backpack ? is_backpack : false,
				isAllselectedId: false,
				boerse: boerse,
				symbol: symbol,
				type: type,
				id: id,
				isChecked: false,
				isForecastLocked: !hasForecast ? true : isForecastAllowed,
				hasForecast: hasForecast,
				isForecastBackpackAuthorized: !hasForecast ? true : isForecastAllowedByBackpack
			};
		}
		return result;
	}, {});
};

export const handleSelectedRows = (ele: string, setSelectedRows: Dispatch<SetStateAction<string[]>>) => {
	setSelectedRows((sr: string[]) => {
		const tempArray = [...sr];
		const index = sr.findIndex((item) => item === ele);
		if (index === -1) {
			tempArray.push(ele);
		} else {
			tempArray.splice(index, 1);
		}
		return tempArray;
	});
};

export const handleCheckboxAndLock = (
	obj: IMaterialObj,
	checked: boolean,
	id: string,
	setMaterialObj: React.Dispatch<React.SetStateAction<IMaterialObj>>,
	isCheckedLock = false,
	isCheckboxSelected = false,
	date: Date | null = null,
	isForecastLocked = false,
	key?: string,
	isItemInQue?: boolean
) => {
	setMaterialObj((materialObj) => {
		const copyOfObj = !isEmpty(obj) ? JSON.parse(JSON.stringify(obj)) : JSON.parse(JSON.stringify(materialObj));
		for (const parentKey in copyOfObj) {
			let isKeyMatched = false;
			const element = copyOfObj[parentKey];

			if (parentKey === id || id === '0group') {
				isKeyMatched = true;
			}
			if (isCheckboxSelected) {
				element.isChecked = isKeyMatched ? checked : element.isChecked;
			} else {
				element.isLocked = isKeyMatched && key !== 'forecast' ? checked : element.isLocked;
				element.isInque = isKeyMatched ? isItemInQue : element.isLocked;
				element.isBackpackAuthorized = !checked && isKeyMatched ? checked : element.isBackpackAuthorized;
				element.isForecastLocked = isKeyMatched ? isForecastLocked : element.isForecastLocked;
			}
			if (has(element, 'children')) {
				for (const childKey in element.children) {
					const childElement = element.children[childKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
					if (parentKey !== id) {
						isKeyMatched = false;
					}
					if (childKey === id || id === '0group') {
						isKeyMatched = true;
					}
					checkboxAndLockRecursion(
						childElement,
						checked,
						id,
						isKeyMatched,
						parentKey,
						childKey,
						date,
						isCheckedLock,
						isCheckboxSelected,
						isForecastLocked,
						isItemInQue as boolean,
						key
					);
				}
				if (every(Object.values(element.children), { isLocked: true })) {
					element.isLocked = true;
				} else {
					element.isLocked = false;
				}
				if (every(Object.values(element.children), { isInque: true })) {
					element.isInque = true;
				} else {
					element.isInque = false;
				}
				if (some(Object.values(element.children), { hasForecast: true })) {
					element.hasForecast = true;
				} else {
					element.hasForecast = false;
				}
				if (every(Object.values(element.children), { isForecastLocked: true })) {
					element.isForecastLocked = true;
				} else {
					element.isForecastLocked = false;
				}
				if (every(Object.values(element.children), { isBackpackAuthorized: true })) {
					element.isBackpackAuthorized = true;
				} else {
					element.isBackpackAuthorized = false;
				}
				if (every(Object.values(element.children), { isForecastBackpackAuthorized: true })) {
					element.isForecastBackpackAuthorized = true;
				} else {
					element.isForecastBackpackAuthorized = false;
				}
				if (isCheckboxSelected) {
					if (every(Object.values(element.children), { isChecked: true })) {
						element.isChecked = true;
					} else {
						element.isChecked = false;
					}
				}
			}
		}

		return copyOfObj;
	});
};

const checkboxAndLockRecursion = (
	obj: IMaterialObjChildren,
	checked: boolean,
	id: string,
	isKeyMatched: boolean,
	parentKey: string,
	childKey: string,
	date: Date | null,
	isCheckedLock: boolean,
	isCheckboxSelected: boolean,
	isForecastLocked: boolean,
	isItemInQue: boolean,
	key?: string
) => {
	if (isCheckboxSelected) {
		obj.isChecked = isKeyMatched ? checked : obj.isChecked;
	} else {
		if ((obj.type === 'group' || obj.type === 'list') && key === 'groupAuthorization') {
			if (!isNull(obj.startDate) && !isNull(obj.endDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
						setValidityLocks(subChildElement);
					}
				} else {
					obj.startDate = null;
					obj.endDate = null;
				}
			} else if (!isNull(obj.startDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
						setValidityLocks(subChildElement);
					}
				} else {
					obj.startDate = null;
				}
			} else if (!isNull(obj.endDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
						setValidityLocks(subChildElement);
					}
				} else {
					obj.endDate = null;
				}
			}
		}
		obj.date = obj.isLocked ? (isCheckedLock ? null : obj.date) : null;
		obj.isLocked = isKeyMatched && key !== 'forecast' ? checked : obj.isLocked;
		obj.isInque = isKeyMatched ? isItemInQue : obj.isInque;
		obj.isForecastLocked = isKeyMatched ? isForecastLocked : obj.isForecastLocked;
		obj.isBackpackAuthorized = !checked && isKeyMatched ? checked : obj.isBackpackAuthorized;
		obj.isForecastBackpackAuthorized = !checked && isKeyMatched ? checked : obj.isForecastBackpackAuthorized;
	}
	if (isCheckboxSelected && obj.date) {
		obj.date = new Date(obj.date);
	}
	if (has(obj, 'children')) {
		for (const subchildKey in obj.children) {
			let copyOfIsKeyMatched = isKeyMatched;
			const subChildElement = obj.children[subchildKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
			if (parentKey !== id && childKey !== id && has(subChildElement, 'children')) {
				copyOfIsKeyMatched = false;
			}
			if (subchildKey === id || id === '0group') {
				copyOfIsKeyMatched = true;
			}
			checkboxAndLockRecursion(
				subChildElement,
				checked,
				id,
				copyOfIsKeyMatched,
				parentKey,
				childKey,
				date,
				isCheckedLock,
				isCheckboxSelected,
				isForecastLocked,
				isItemInQue,
				key
			);
		}
		if (has(obj, 'children')) {
			if (every(Object.values(obj.children), { isLocked: true })) {
				obj.isLocked = true;
			} else {
				obj.isLocked = false;
			}
		}
		if (has(obj, 'children')) {
			if (every(Object.values(obj.children), { isInque: true })) {
				obj.isInque = true;
			} else {
				obj.isInque = false;
			}
		}
		if (has(obj, 'children')) {
			if (some(Object.values(obj.children), { hasForecast: true })) {
				obj.hasForecast = true;
			} else {
				obj.hasForecast = false;
			}
		}
		if (has(obj, 'children')) {
			if (every(Object.values(obj.children), { isForecastLocked: true })) {
				obj.isForecastLocked = true;
			} else {
				obj.isForecastLocked = false;
			}
		}
		if (every(Object.values(obj.children), { isBackpackAuthorized: true })) {
			obj.isBackpackAuthorized = true;
		} else {
			obj.isBackpackAuthorized = false;
		}

		if (every(Object.values(obj.children), { isForecastBackpackAuthorized: true })) {
			obj.isForecastBackpackAuthorized = true;
		} else {
			obj.isForecastBackpackAuthorized = false;
		}
		if (isCheckboxSelected && !isEmpty(obj.children)) {
			if (every(Object.values(obj.children), { isChecked: true })) {
				obj.isChecked = true;
			} else {
				obj.isChecked = false;
			}
		}
	} else {
		if (`${obj.id}${obj.type}` === id && !isCheckboxSelected) {
			if (key === 'startDate') {
				obj.startDate = key && key === 'startDate' ? date : new Date(obj.startDate as any);
			} else if (key === 'endDate') {
				obj.endDate = key && key === 'endDate' ? date : new Date(obj.endDate as any);
			} else {
				obj.startDate = !obj.isInque ? null : obj.startDate;
				obj.endDate = !obj.isInque && !obj.isLocked ? null : obj.endDate;
			}
		}
	}
};

type GroupData = INewsGroupData | IForecastGroupData | IPdfModuleGroupData;
export type CostModelGroupData = ICostModelGroup | ICostModelList | ICostModelListEntry;

const setValidityLocks = (obj: IMaterialObjChildren) => {
	if (has(obj, 'children')) {
		for (const childKey in obj.children) {
			const subChildElement = obj.children[childKey as keyof IMaterialObjChildren] as IMaterialObjChildren;
			setValidityLocks(subChildElement);
		}
	} else {
		if (!isNull(obj.startDate) && !isNull(obj.endDate)) {
			obj.startDate = null;
			obj.endDate = null;
		} else if (!isNull(obj.startDate)) {
			obj.startDate = null;
		} else if (!isNull(obj.endDate)) {
			obj.endDate = null;
		} else {
			return obj;
		}
	}
};

export const convertArrayToKeyValueForNewsAndForecast = (arr: GroupData[], section: string) => {
	return arr.reduce((result: any, item: GroupData) => {
		const { id, children, type, is_backpack, status, gueltig_ab, gueltig_bis } = item;
		const childrenObject =
			get(item, 'children', []).length === 0 ? false : convertArrayToKeyValueForNewsAndForecast(children as GroupData[], section);
		if (!isEmpty(get(item, 'children', []))) {
			result[`${id}${type}`] = {
				isAllselectedId: false,
				children: childrenObject,
				type: item.type,
				isChecked: false,
				isLocked: false
			};
		} else {
			result[`${id}${type}`] = {
				startDate: gueltig_ab ? new Date(gueltig_ab) : null,
				endDate: gueltig_bis ? new Date(gueltig_bis) : null,
				isChecked: false,
				isAllselectedId: false,
				id: id,
				type: item.type,
				isBackpackAuthorized: is_backpack ? is_backpack : false,
				isLocked: status === 'A',
				isInque: status === 'Q'
			};
		}
		return result;
	}, {});
};

export const convertArrayToKeyValueForCostModel = (arr: CostModelGroupData[], parentIsMyModel = false) => {
	return arr.reduce((result: any, item: CostModelGroupData) => {
		const { id, children, type, is_backpack, status, startDate, endDate, boerse, symbol } = item;
		const shouldSetChildrenMyModel = (type === 'group' && id === -1) || parentIsMyModel;
		const childrenObject =
			get(item, 'children', []).length === 0 ? false : convertArrayToKeyValueForCostModel(children, shouldSetChildrenMyModel);

		if (type === 'group') {
			result[`${id}${type}`] = {
				isAllselectedId: false,
				children: childrenObject,
				type: item.type,
				isChecked: false,
				isLocked: every(item.children, { status: 'A' }),
				isInque: every(item.children, { status: 'Q' }),
				id: id,
				isMyModel: id === -1 ? true : false
			};
		} else {
			result[`${id}${type}`] = {
				startDate: startDate ? new Date(startDate) : null,
				endDate: endDate ? new Date(endDate) : null,
				isChecked: false,
				isAllselectedId: false,
				id: id,
				type: item.type,
				isBackpackAuthorized: is_backpack ? is_backpack : false,
				isLocked: status === 'A',
				isInque: status === 'Q',
				children: childrenObject,
				boerse: boerse,
				symbol: symbol,
				isMyModel: type !== 'list_entries' && shouldSetChildrenMyModel
			};
		}
		return result;
	}, {});
};

export const findTotalLocks = (obj: IMaterialObjChildren, activeTab: string): { totalCount: number; totalLocks: number } => {
	let totalCount = 0;
	let totalLocks = 0;

	const traverseTree = (node: IMaterialObjChildren) => {
		if ((activeTab === 'material' && node.type === 'list_entries') || (activeTab === 'news' && node.type === 'list')) {
			totalCount++;
			if (node.isLocked) {
				totalLocks++;
			}
		}
		if (!isEmpty(node.children)) {
			for (const key in node.children) {
				if (Object.prototype.hasOwnProperty.call(node.children, key)) {
					const childNode = node.children[key as keyof IMaterialObjChildren] as IMaterialObjChildren;
					traverseTree(childNode);
				}
			}
		}
	};
	traverseTree(obj);
	return { totalCount, totalLocks };
};

export const handleNameKey = (name_deu: string, name_eng: string, name: string, language: string) => {
	const nameMap: { [key: string]: string[] } = {
		de: [name_deu, name],
		en: [name_eng, name_deu, name]
	};

	const names = nameMap[language];
	if (names) {
		for (const n of names) {
			if (n) {
				return n;
			}
		}
	}

	return '-';
};

export const extractGroupData = (data: (IAllMaterialType | IAllNewsType)[], language: string) => {
	const result: IDropDownOption[] = [];
	data.forEach((group) => {
		function traverseChildren(children: (IAllMaterialType | IAllNewsType)[], groupStr: string) {
			children.forEach((child) => {
				const label = `${groupStr}>${(language === 'en' && child.name_eng !== '' && child.name_eng) || child.name}`;
				if (child.type === 'group') {
					result.push({
						label: label,
						value: child.id
					});
					if (child.children.length) {
						traverseChildren(child.children, label);
					}
				}
			});
		}
		const groupStr = (language === 'en' && group.name_eng !== '' && group.name_eng) || group.name;
		if (group.type === 'group' && group.id !== 0) {
			result.push({
				label: groupStr,
				value: group.id
			});
		}
		traverseChildren(group.children, groupStr);
	});
	return result;
};

interface ICostModelObjValue {
	id: number;
	isLocked: boolean;
	isInque: boolean;
	isChecked: boolean;
	isBackpackAuthorized?: boolean;
	startDate?: Date | null;
	endDate?: Date | null;
	children?: { [key: string]: ICostModelObjValue };
	type: string;
}

export const handleCheckboxAndLockCostModel = (
	obj: ICostModelObj,
	checked: boolean,
	id: string,
	setCostModelObj: React.Dispatch<React.SetStateAction<ICostModelObj>>,
	isCheckedLock = false,
	isCheckboxSelected = false,
	date: Date | null = null,
	key?: string,
	objType?: string,
	isItemInQue = false
) => {
	setCostModelObj((costModelObj) => {
		const copyOfObj = !isEmpty(obj) ? JSON.parse(JSON.stringify(obj)) : JSON.parse(JSON.stringify(costModelObj));
		for (const parentKey in copyOfObj) {
			let isKeyMatched = false;
			const element = copyOfObj[parentKey] as ICostModelObjValue;

			if (parentKey === id || id === 'allgroup') {
				isKeyMatched = true;
			}
			if (isCheckboxSelected) {
				element.isChecked = isKeyMatched ? checked : element.isChecked;
			} else {
				element.isLocked = isKeyMatched ? checked : element.isLocked;
				element.isInque = isKeyMatched ? isItemInQue : element.isLocked;
				element.isBackpackAuthorized = !checked && isKeyMatched ? checked : element.isBackpackAuthorized;
			}
			if (element.children) {
				for (const childKey in element.children) {
					const childElement = element.children[childKey];
					if (parentKey !== id) {
						isKeyMatched = false;
					}
					if (childKey === id || id === 'allgroup') {
						isKeyMatched = true;
					}
					checkboxAndLockRecursionCostModel(
						childElement,
						checked,
						id,
						isKeyMatched,
						parentKey,
						childKey,
						date,
						isCheckedLock,
						isCheckboxSelected,
						isItemInQue as boolean,
						key,
						objType
					);
				}
				if (element.type === 'group') {
					// pick only the direct <list> children
					const listChildren = Object.values(element.children).filter((child) => child.type === 'list');

					// group is locked iff there’s at least one <list> and all of them are locked
					element.isLocked = listChildren.length > 0 && listChildren.every((child) => child.isLocked);

					// group is in-queue iff at least one <list> and all of them are in-queue
					element.isInque = listChildren.length > 0 && listChildren.every((child) => child.isInque);

					// group is backpack-authorized iff at least one <list> and all of them are backpack-authorized
					element.isBackpackAuthorized = listChildren.length > 0 && listChildren.every((child) => child.isBackpackAuthorized);
				} else if (element.type === 'list') {
					// for <list>, you still want to look at all its entries
					const entryValues = Object.values(element.children);
					element.isLocked = entryValues.every((e) => e.isLocked);
					element.isInque = entryValues.every((e) => e.isInque);
					element.isBackpackAuthorized = entryValues.every((e) => e.isBackpackAuthorized);
				}
			}
			if (isCheckboxSelected && element.children) {
				if (every(Object.values(element.children), { isChecked: true })) {
					element.isChecked = true;
				} else {
					element.isChecked = false;
				}
			}
		}

		return copyOfObj;
	});
};

const checkboxAndLockRecursionCostModel = (
	obj: ICostModelObjValue,
	checked: boolean,
	id: string,
	isKeyMatched: boolean,
	parentKey: string,
	childKey: string,
	date: Date | null,
	isCheckedLock: boolean,
	isCheckboxSelected: boolean,
	isItemInQue: boolean,
	key?: string,
	objType?: string
) => {
	if (isCheckboxSelected) {
		obj.isChecked = isKeyMatched ? checked : obj.isChecked;
	} else {
		if (key === 'groupAuthorization') {
			if (!isNull(obj.startDate) && !isNull(obj.endDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof ICostModelObj[string]] as ICostModelObj[string];
						setValidityLocksCostModel(subChildElement);
					}
				} else {
					obj.startDate = null;
					obj.endDate = null;
				}
			} else if (!isNull(obj.startDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof ICostModelObj[string]] as ICostModelObj[string];
						setValidityLocksCostModel(subChildElement);
					}
				} else {
					obj.startDate = null;
				}
			} else if (!isNull(obj.endDate)) {
				if (has(obj, 'children')) {
					for (const childKey in obj.children) {
						const subChildElement = obj.children[childKey as keyof ICostModelObj[string]] as ICostModelObj[string];
						setValidityLocksCostModel(subChildElement);
					}
				} else {
					obj.endDate = null;
				}
			}
		}
		obj.isLocked = isKeyMatched ? checked : obj.isLocked;
		obj.isInque = isKeyMatched ? isItemInQue : obj.isInque;
		obj.isBackpackAuthorized = !checked && isKeyMatched ? checked : obj.isBackpackAuthorized;
	}
	if (obj.children) {
		if (objType !== 'list') {
			for (const subchildKey in obj.children) {
				let copyOfIsKeyMatched = isKeyMatched;
				const subChildElement = obj.children[subchildKey];
				if (parentKey !== id && childKey !== id && subChildElement.children) {
					copyOfIsKeyMatched = false;
				}
				if (subchildKey === id || id === 'allgroup') {
					copyOfIsKeyMatched = true;
				}
				checkboxAndLockRecursionCostModel(
					subChildElement,
					checked,
					id,
					copyOfIsKeyMatched,
					parentKey,
					childKey,
					date,
					isCheckedLock,
					isCheckboxSelected,
					isItemInQue,
					key
				);
				if (subChildElement.type === 'group') {
					// pick only the direct <list> children
					const listChildren = Object.values(obj.children)?.filter((child) => child.type === 'list');

					// group is locked iff there’s at least one <list> and all of them are locked
					subChildElement.isLocked = listChildren.length > 0 && listChildren.every((child) => child.isLocked);

					// group is in-queue iff at least one <list> and all of them are in-queue
					subChildElement.isInque = listChildren.length > 0 && listChildren.every((child) => child.isInque);

					// group is backpack-authorized iff at least one <list> and all of them are backpack-authorized
					subChildElement.isBackpackAuthorized = listChildren.length > 0 && listChildren.every((child) => child.isBackpackAuthorized);
				} else if (subChildElement.type === 'list') {
					// for <list>, you still want to look at all its entries
					const entryValues = Object.values(obj.children);
					subChildElement.isLocked = entryValues.every((e) => e.isLocked);
					subChildElement.isInque = entryValues.every((e) => e.isInque);
					subChildElement.isBackpackAuthorized = entryValues.every((e) => e.isBackpackAuthorized);
				}
			}
		}
	}
	if (isCheckboxSelected && !isEmpty(obj.children)) {
		const childrenValues = Object.values(obj.children);
		if (every(childrenValues, { isChecked: true })) {
			obj.isChecked = true;
		} else {
			obj.isChecked = false;
		}
	}
	if (`${obj.id}${obj.type}` === id && !isCheckboxSelected) {
		if (key === 'startDate') {
			obj.startDate = key && key === 'startDate' ? date : new Date(obj.startDate as any);
		} else if (key === 'endDate') {
			obj.endDate = key && key === 'endDate' ? date : new Date(obj.endDate as any);
		} else {
			obj.startDate = !obj.isInque ? null : obj.startDate;
			obj.endDate = !obj.isInque && !obj.isLocked ? null : obj.endDate;
		}
	}
};

const setValidityLocksCostModel = (obj: ICostModelObjValue) => {
	if (has(obj, 'children')) {
		for (const childKey in obj.children) {
			const subChildElement = obj.children[childKey as keyof ICostModelObj[string]] as ICostModelObj[string];
			setValidityLocksCostModel(subChildElement);
		}
	} else {
		if (!isNull(obj.startDate) && !isNull(obj.endDate)) {
			obj.startDate = null;
			obj.endDate = null;
		} else if (!isNull(obj.startDate)) {
			obj.startDate = null;
		} else if (!isNull(obj.endDate)) {
			obj.endDate = null;
		} else {
			return obj;
		}
	}
};

export const downloadUserList = (setLoading: (action: boolean) => void) => {
	setLoading(true);
	httpService
		.get(`${API_CONFIG.path.downloadUserList}`, undefined, { responseType: 'arraybuffer' })
		.then((response) => {
			const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			saveAs(blob, `${response.headers['content-disposition'].split('=')[1]}`);
			setLoading(false);
		})
		.catch((error) => {
			console.error('error', error);
			setLoading(false);
		});
};

export const downloadLogins = (setActionLoading: (action: boolean) => void) => {
	setActionLoading(true);
	httpService
		.get(`${API_CONFIG.path.downloadLogins}`)
		.then((response) => {
			exportUserLogins(response);
			setActionLoading(false);
		})
		.catch((error) => {
			console.error('error', error);
			setActionLoading(false);
		});
};

export const exportUserLogins = async (data: ILoginsInfo[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('User');
	worksheet.columns = downloadUserLogins;
	data.forEach((item: any) => {
		const { benutzer_id, email, anrede, vorname, nachname, lastLoginDate, lastLoginTime, lastSeen } = item;

		const loginDate = lastLoginDate ? moment(lastLoginDate).format('DD.MM.YYYY') : '';
		const loginTime = lastLoginTime ? moment(lastLoginTime).format('HH:mm:ss') : '';
		worksheet.addRow({
			benutzer_id,
			email,
			anrede,
			vorname,
			nachname,
			lastLoginDate: loginDate,
			lastLoginTime: loginTime,
			lastSeen
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Login_Overview_${formatDate(new Date(), 'YYYYMMDDHHmm')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportCurrenciesData = async (data: any) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Waehrunge');
	worksheet.columns = downloadCurrenciesHeader;
	data.forEach((item: any) => {
		const { waehrung, wfaktor, name_de, name_en, sichtbar, sortidx } = item;
		worksheet.addRow({
			waehrung,
			wfaktor,
			name_de,
			name_en,
			sichtbar,
			sortidx
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Waehrungen--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportUnitsData = async (data: IUnitInterface[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Einheiten');
	worksheet.columns = downloadUnitsHeaders;
	data.forEach((item) => {
		const { einheit, name, name_en, efaktor, egruppe, sichtbar, egruppe_bez } = item;
		worksheet.addRow({
			einheit,
			name,
			name_en,
			efaktor,
			egruppe,
			sichtbar,
			egruppe_bez
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Einheiten--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportCountriesAndRegionsData = async (data: any) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Laender-und-Regionen');
	worksheet.columns = downloadCountriesAndRegions;
	data.forEach((item: any) => {
		const { land, name_de, region, sichtbar, sortidx } = item;
		worksheet.addRow({
			land,
			name_de,
			region,
			sichtbar,
			sortidx
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Laender-und-Regionen--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportNotationData = async (data: any) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Notierungen');
	worksheet.columns = downloadNotations;
	data.forEach((item: any) => {
		const { notierung, name, name_en, sortidx } = item;
		worksheet.addRow({
			notierung,
			name,
			name_en,
			sortidx
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Notierungen--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportTendersCountries = async (data: any) => {
	const workbook = new ExcelJS.Workbook();

	Object.keys(data).forEach((key) => {
		const items = data[key];
		if (Array.isArray(items)) {
			// Create a worksheet for each key
			const worksheet = workbook.addWorksheet(key);

			const columns = downloadTenadersCountriesHeaders[key];
			worksheet.columns = columns;

			// Add rows dynamically
			items.forEach((item) => {
				worksheet.addRow(item);
			});
		}
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Country_list_${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportAdminMasterData = async (data: IMaterDataDownload[], fileFormat: string) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Stammdaten--mbi');
	worksheet.columns = downloadAdminMasterDataHeaders;
	data.forEach((item: IMaterDataDownload) => {
		const {
			symbol,
			boerse,
			basis_symbol,
			isin,
			faelligkeit,
			faelligkeit_f,
			frei,
			sichtbar,
			editierbar_,
			land,
			waehrung,
			waehrung_ignore,
			einheit,
			notierung,
			notierung_zusatz,
			nkstellen,
			max_kursalter,
			name_deu,
			name_eng,
			name_zusatz,
			boersenname,
			datenlieferant,
			quelle,
			kunde,
			aid_kommentar_,
			prozent_abweichung_erlaubt,
			verantwortlich,
			info_de,
			info_en,
			priority,
			link,
			entfernt_am,
			hinzugefuegt_am,
			lagerbestand_boerse,
			lagerbestand_symbol,
			notierung_zusatz_en
		} = item;
		worksheet.addRow({
			symbol,
			boerse,
			basis_symbol,
			isin,
			faelligkeit,
			faelligkeit_f,
			frei,
			sichtbar,
			editierbar_,
			land,
			waehrung,
			waehrung_ignore,
			einheit,
			notierung,
			notierung_zusatz,
			nkstellen,
			max_kursalter,
			name_deu,
			name_eng,
			name_zusatz,
			boersenname,
			datenlieferant,
			quelle,
			kunde,
			aid_kommentar_,
			prozent_abweichung_erlaubt,
			verantwortlich,
			info_de,
			info_en,
			priority,
			link,
			entfernt_am,
			hinzugefuegt_am,
			lagerbestand_boerse,
			lagerbestand_symbol,
			notierung_zusatz_en
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const csvData = Papa.unparse(data);
	const csvBlob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
	const downloadSheetName = `Stammdaten--mbi--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	if (fileFormat === 'csv') {
		saveAs(csvBlob, `${downloadSheetName}.csv`);
	} else {
		saveAs(new Blob([buf]), `${downloadSheetName}.${fileFormat}`);
	}
};

export const exportNewsData = async (data: any, userId: number) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Stammdaten');
	worksheet.columns = downloadHeader;
	data.forEach((item: any) => {
		const { grusel, kategorie, kategorie_eng, kategorie_id } = item;
		worksheet.addRow({
			kategorie_id,
			kategorie,
			kategorie_eng,
			grusel
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Nachrichten--Stammdaten--Benutzer-${userId}--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportMaterialGroup = async (data: any) => {
	const workbook = new ExcelJS.Workbook();

	Object.keys(data).forEach((key) => {
		const items = data[key];
		if (Array.isArray(items)) {
			// Create a worksheet for each key
			const worksheet = workbook.addWorksheet(key);

			const columns = downloadMaterialGroupHeaders[key];
			worksheet.columns = columns;

			// Add rows dynamically
			items.forEach((item) => {
				worksheet.addRow(item);
			});
		}
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Matflixx_Datenstruktur--${formatDate(new Date(), 'YYYY-MM-DD--hh-mm')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportAuthorization = async (data: any, name: string, type: string) => {
	const workbook = new ExcelJS.Workbook();

	Object.keys(data).forEach((key) => {
		const items = data[key];
		if (Array.isArray(items)) {
			// Create a worksheet for each key
			const worksheet = workbook.addWorksheet(key);

			const columns = type === 'user' ? downloadAuthorizationUser[key] : downloadAuthorizationBackpack[key];
			worksheet.columns = columns;

			// Add rows dynamically
			items.forEach((item) => {
				worksheet.addRow(item);
			});
		}
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Matflixx_Berechtigungen_${name}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const adminDataDownload = async (data: any, symbol: string, boerse: string) => {
	const workbook = new ExcelJS.Workbook();

	Object.keys(data).forEach((key) => {
		const items = data[key];
		if (Array.isArray(items)) {
			// Create a worksheet for each key
			const worksheet = workbook.addWorksheet(key);

			const columns = downloadAdminDataMaterial[key];
			worksheet.columns = columns;

			// Add rows dynamically
			items.forEach((item) => {
				worksheet.addRow(item);
			});
		}
	});
	const buf = await workbook.xlsx.writeBuffer();
	const safeSymbol = symbol.replace(/\//g, '=').replace(/\|/g, '==');
	const downloadSheetName = `Matflixx_materialbereich_(${safeSymbol})_(${boerse})`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};
